import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { format, addDays, eachDayOfInterval, differenceInDays } from 'date-fns';

const GanttChart = ({ projects, onProjectClick, viewMode }) => {
  const [scrollPosition, setScrollPosition] = useState(0);
  const chartRef = useRef(null);
  const today = new Date();

  const getDaysInView = () => {
    switch (viewMode) {
      case 'weekly':
        return 14;
      case 'monthly':
        return 60;
      case 'quarterly':
        return 180;
      default:
        return 60;
    }
  };

  const daysInView = getDaysInView();
  const startDate = addDays(today, -Math.floor(daysInView / 3));
  const endDate = addDays(startDate, daysInView);
  const dateRange = eachDayOfInterval({ start: startDate, end: endDate });

  const getProjectStatus = (deliveryDate) => {
    const daysUntilDelivery = differenceInDays(new Date(deliveryDate), today);
    
    if (daysUntilDelivery < 0) {
      return { status: 'overdue', color: 'bg-red-200', textColor: 'text-red-900' };
    } else if (daysUntilDelivery <= 7) {
      return { status: 'due-soon', color: 'bg-yellow-200', textColor: 'text-yellow-900' };
    } else {
      return { status: 'on-track', color: 'bg-green-200', textColor: 'text-green-900' };
    }
  };

  const calculateProjectPosition = (startDate, deliveryDate) => {
    const projectStart = new Date(startDate);
    const projectEnd = new Date(deliveryDate);
    
    const startOffset = differenceInDays(projectStart, startDate);
    const duration = differenceInDays(projectEnd, projectStart);
    
    const dayWidth = 100 / daysInView;
    const left = Math.max(0, startOffset * dayWidth);
    const width = Math.max(dayWidth, duration * dayWidth);
    
    return { left: `${left}%`, width: `${width}%` };
  };

  const handleScroll = (direction) => {
    if (chartRef?.current) {
      const scrollAmount = 200;
      const newPosition = direction === 'left' 
        ? Math.max(0, scrollPosition - scrollAmount)
        : scrollPosition + scrollAmount;
      
      chartRef.current.scrollLeft = newPosition;
      setScrollPosition(newPosition);
    }
  };

  const todayPosition = () => {
    const daysSinceStart = differenceInDays(today, startDate);
    const dayWidth = 100 / daysInView;
    return `${daysSinceStart * dayWidth}%`;
  };

  return (
    <div className="relative bg-white rounded-xl shadow-elevation-2 overflow-hidden">
      <div className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4 border-b border-border">
        <div className="flex items-center gap-2 md:gap-3">
          <Icon name="Calendar" size={20} className="text-primary" />
          <h3 className="text-base md:text-lg font-semibold text-foreground">Project Timeline</h3>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleScroll('left')}
            className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-muted hover:bg-muted/80 flex items-center justify-center transition-smooth"
            aria-label="Scroll left"
          >
            <Icon name="ChevronLeft" size={18} />
          </button>
          <button
            onClick={() => handleScroll('right')}
            className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-muted hover:bg-muted/80 flex items-center justify-center transition-smooth"
            aria-label="Scroll right"
          >
            <Icon name="ChevronRight" size={18} />
          </button>
        </div>
      </div>
      <div 
        ref={chartRef}
        className="overflow-x-auto overflow-y-visible"
        style={{ scrollBehavior: 'smooth' }}
      >
        <div className="min-w-[800px] lg:min-w-[1200px] p-4 md:p-6">
          <div className="relative">
            <div className="flex border-b border-border pb-2 mb-4">
              {dateRange?.map((date, index) => (
                <div
                  key={index}
                  className="flex-1 text-center"
                  style={{ minWidth: `${100 / daysInView}%` }}
                >
                  <div className="text-xs text-muted-foreground">
                    {format(date, 'MMM d')}
                  </div>
                  {viewMode === 'weekly' && (
                    <div className="text-xs text-muted-foreground mt-1">
                      {format(date, 'EEE')}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="relative" style={{ minHeight: `${projects?.length * 80}px` }}>
              <div
                className="absolute top-0 bottom-0 w-0.5 bg-primary z-10"
                style={{ left: todayPosition() }}
              >
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-2 py-1 rounded text-xs whitespace-nowrap">
                  Today
                </div>
              </div>

              {projects?.map((project, index) => {
                const { status, color, textColor } = getProjectStatus(project?.deliveryDate);
                const position = calculateProjectPosition(project?.startDate, project?.deliveryDate);
                
                return (
                  <div
                    key={project?.id}
                    className="relative mb-4"
                    style={{ height: '60px' }}
                  >
                    <div className="absolute left-0 top-0 w-full h-full flex items-center">
                      <div className="w-32 md:w-40 pr-4 flex-shrink-0">
                        <div className="text-sm font-medium text-foreground truncate">
                          {project?.clientName}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          ₹{project?.projectValue?.toLocaleString()}
                        </div>
                      </div>
                      
                      <div className="flex-1 relative">
                        <button
                          onClick={() => onProjectClick(project)}
                          className={`absolute ${color} ${textColor} rounded-lg px-3 md:px-4 py-2 md:py-3 shadow-elevation-1 hover:shadow-elevation-2 transition-smooth cursor-pointer group`}
                          style={{
                            left: position?.left,
                            width: position?.width,
                            minWidth: '120px'
                          }}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="text-xs md:text-sm font-medium truncate">
                                {project?.clientName}
                              </div>
                              <div className="text-xs opacity-80 truncate">
                                {format(new Date(project.startDate), 'MMM d')} - {format(new Date(project.deliveryDate), 'MMM d')}
                              </div>
                            </div>
                            <Icon 
                              name="Edit2" 
                              size={14} 
                              className="opacity-0 group-hover:opacity-100 transition-smooth flex-shrink-0"
                            />
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GanttChart;